import { useEffect, useRef } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import {
  loadLaterItems,
  loadScheduledSends,
  markReminderNotified,
  removeScheduledSend,
} from "./later";

/**
 * Fires due reminders (notification + toast) and delivers due scheduled
 * sends. Client-side by design for v1 — runs a sweep on load (catching
 * anything past due while the app was closed) and every 30s after.
 */
export function useLaterEngine(onToast: (msg: string) => void) {
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;

  useEffect(() => {
    const sweep = () => {
      const now = Date.now();

      for (const item of loadLaterItems()) {
        if (item.kind !== "reminder" || item.notified || !item.remindAt || item.remindAt > now) continue;
        markReminderNotified(item.id);
        onToastRef.current(`Reminder: ${item.text.slice(0, 80)}`);
        try {
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Reminder — dbm. chat", { body: item.text.slice(0, 120), icon: "/favicon.svg" });
          }
        } catch {
          /* ignore */
        }
      }

      for (const send of loadScheduledSends()) {
        if (send.sendAt > now) continue;
        removeScheduledSend(send.id); // remove first so a failure can't double-send on next sweep
        const msg = new CometChat.TextMessage(send.target.id, send.text, send.target.kind);
        CometChat.sendMessage(msg)
          .then(() => onToastRef.current(`Scheduled message sent to ${send.target.name}`))
          .catch((e) => {
            console.warn("scheduled send failed", e);
            onToastRef.current(`Couldn't send scheduled message to ${send.target.name}`);
          });
      }
    };

    sweep();
    const interval = window.setInterval(sweep, 30_000);
    return () => window.clearInterval(interval);
  }, []);
}
